import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import EtaHint from '../components/EtaHint';
import { pushToast } from '../hooks/useToasts';
const PROFILE_NAMES = ['safe', 'balanced', 'capacity', 'youtube-strict'];
export default function Encode() {
    const { t } = useTranslation();
    const nav = useNavigate();
    const [file, setFile] = useState(null);
    // '' = personalizado: se envían los campos QR explícitos como siempre.
    // Con perfil: se envía SOLO el perfil (el CLI resuelve los parámetros) y los
    // campos QR se muestran con los valores del perfil, deshabilitados.
    const [profile, setProfile] = useState('');
    const [profileDefs, setProfileDefs] = useState(null);
    const [qrVersion, setQrVersion] = useState(40);
    const [qrLevel, setQrLevel] = useState('H');
    const [payloadBytes, setPayloadBytes] = useState(700);
    const [qrsPerFrame, setQrsPerFrame] = useState(1);
    const [repeat, setRepeat] = useState(3);
    const [fps, setFps] = useState(24);
    const [drawMarkers, setDrawMarkers] = useState(true);
    const [drawInfo, setDrawInfo] = useState(true);
    const [whiteBackground, setWhiteBackground] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => {
        api.get('/profiles')
            .then((r) => setProfileDefs(r.data))
            .catch(() => setProfileDefs(null)); // sin perfiles: el selector sigue usable
    }, []);
    const applied = profile && profileDefs?.[profile];
    useEffect(() => {
        if (!applied)
            return;
        setQrVersion(applied.qr_version);
        setQrLevel(applied.qr_level);
        setPayloadBytes(applied.payload_bytes);
        setQrsPerFrame(applied.qrs_per_frame);
        setRepeat(applied.repeat);
    }, [applied]);
    const onSubmit = async (e) => {
        e.preventDefault();
        if (!file)
            return;
        setBusy(true);
        setError(null);
        const fd = new FormData();
        fd.append('file', file);
        const params = profile
            ? { profile, fps, drawMarkers, drawInfo, whiteBackground }
            : { qrVersion, qrLevel, payloadBytes, qrsPerFrame, repeat, fps,
                drawMarkers, drawInfo, whiteBackground };
        fd.append('params', JSON.stringify(params));
        try {
            await api.post('/jobs/encode', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            pushToast({ kind: 'info', title: t('jobLaunched.title'), msg: t('jobLaunched.msg') });
            nav('/dashboard');
        }
        catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
        finally {
            setBusy(false);
        }
    };
    const locked = profile !== '';
    return (_jsxs("section", { className: "prose", children: [_jsx("h1", { children: t('encode.title') }), _jsxs("form", { onSubmit: onSubmit, className: "card form-card", children: [_jsxs("label", { children: [t('encode.fileLabel'), _jsx("input", { type: "file", required: true, onChange: (e) => setFile(e.target.files?.[0] ?? null) })] }), _jsxs("label", { children: [t('encode.params.profile'), _jsxs("select", { value: profile, onChange: (e) => setProfile(e.target.value), children: [_jsx("option", { value: "", children: t('encode.profiles.custom') }), PROFILE_NAMES.map((p) => (_jsxs("option", { value: p, children: [p, " \u2014 ", t(`encode.profiles.${p}`)] }, p)))] })] }), _jsxs("div", { className: "grid-2", children: [_jsxs("label", { children: [t('encode.params.qrVersion'), _jsx("input", { type: "number", min: 1, max: 40, value: qrVersion, disabled: locked, onChange: (e) => setQrVersion(Number(e.target.value)) })] }), _jsxs("label", { children: [t('encode.params.qrLevel'), _jsx("select", { value: qrLevel, disabled: locked, onChange: (e) => setQrLevel(e.target.value), children: ['L', 'M', 'Q', 'H'].map((l) => _jsx("option", { value: l, children: l }, l)) })] }), _jsxs("label", { children: [t('encode.params.payloadBytes'), _jsx("input", { type: "number", min: 64, max: 2000, value: payloadBytes, disabled: locked, onChange: (e) => setPayloadBytes(Number(e.target.value)) })] }), _jsxs("label", { children: [t('encode.params.qrsPerFrame'), _jsx("input", { type: "number", min: 1, max: 16, value: qrsPerFrame, disabled: locked, onChange: (e) => setQrsPerFrame(Number(e.target.value)) })] }), _jsxs("label", { children: [t('encode.params.repeat'), _jsx("input", { type: "number", min: 1, max: 30, value: repeat, disabled: locked, onChange: (e) => setRepeat(Number(e.target.value)) })] }), _jsxs("label", { children: [t('encode.params.fps'), _jsx("input", { type: "number", min: 1, max: 120, value: fps, onChange: (e) => setFps(Number(e.target.value)) })] })] }), _jsxs("div", { className: "grid-2", children: [_jsxs("label", { className: "checkbox-label", children: [_jsx("input", { type: "checkbox", checked: drawMarkers, onChange: (e) => setDrawMarkers(e.target.checked) }), t('encode.params.drawMarkers')] }), _jsxs("label", { className: "checkbox-label", children: [_jsx("input", { type: "checkbox", checked: drawInfo, onChange: (e) => setDrawInfo(e.target.checked) }), t('encode.params.drawInfo')] }), _jsxs("label", { className: "checkbox-label", children: [_jsx("input", { type: "checkbox", checked: whiteBackground, onChange: (e) => setWhiteBackground(e.target.checked) }), t('encode.params.whiteBackground')] })] }), error && _jsx("p", { className: "error", children: error }), _jsx(EtaHint, { kind: "encode" }), _jsx("button", { type: "submit", className: "btn btn-primary", disabled: busy || !file, children: busy ? t('common.loading') : t('encode.submit') })] })] }));
}
